import React, { useState, useEffect } from 'react'
import { CashAccount } from '../types'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const CashAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<CashAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState<CashAccount | null>(null)
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<CashAccount | null>(null)
  const [documents, setDocuments] = useState<any[]>([])
  const [loadingDocuments, setLoadingDocuments] = useState(false)
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [selectedAccountJson, setSelectedAccountJson] = useState<CashAccount | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'CASH',
    currency: 'MDL',
    isActive: true,
    isDefault: false
  })

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      const data = await apiGet<CashAccount[]>('/cash-accounts')
      setAccounts(data)
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки счетов')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingAccount) {
        await apiPut(`/cash-accounts/${editingAccount.id}`, formData)
      } else {
        await apiPost('/cash-accounts', formData)
      }
      
      await fetchAccounts()
      handleCloseForm()
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения счета')
    }
  }

  const handleEdit = (account: CashAccount) => {
    setEditingAccount(account)
    setFormData({
      name: account.name,
      type: account.type,
      currency: account.currency,
      isActive: account.isActive,
      isDefault: account.isDefault
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот счет?')) return
    
    try {
      await apiDelete(`/cash-accounts/${id}`)
      await fetchAccounts()
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления счета')
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingAccount(null)
    setFormData({ name: '', type: 'CASH', currency: 'MDL', isActive: true, isDefault: false })
  }

  const fetchDocuments = async (accountId: string, documentType: string) => {
    try {
      setLoadingDocuments(true)
      const data = await apiGet<any[]>(`/cash-accounts/${accountId}/documents/${documentType}`)
      setDocuments(data)
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки документов')
    } finally {
      setLoadingDocuments(false)
    }
  }

  const handleDocumentClick = async (account: CashAccount, documentType: string, count: number) => {
    if (count === 0) return
    
    setSelectedAccount(account)
    setShowDocumentsModal(true)
    await fetchDocuments(account.id, documentType)
  }

  const closeDocumentsModal = () => {
    setShowDocumentsModal(false)
    setSelectedAccount(null)
    setDocuments([])
  }

  const handleShowJson = (account: CashAccount) => {
    setSelectedAccountJson(account)
    setShowJsonModal(true)
  }

  const closeJsonModal = () => {
    setShowJsonModal(false)
    setSelectedAccountJson(null)
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'CASH': return 'Касса'
      case 'BANK': return 'Банковский счет'
      case 'CARD': return 'Банковская карта'
      case 'EXTERNAL': return 'Внешний счет'
      default: return type
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'CASH': return 'bg-green-100 text-green-800'
      case 'BANK': return 'bg-blue-100 text-blue-800'
      case 'CARD': return 'bg-purple-100 text-purple-800'
      case 'EXTERNAL': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatBalance = (balance: number, currency: string) => {
    return `${balance.toFixed(2)} ${currency}`
  }

  if (loading) return <div className="p-6">Загрузка...</div>
  if (error) return <div className="p-6 text-red-600">Ошибка: {error}</div>

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Справочник счетов</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Добавить счет
        </button>
      </div>

      {/* Таблица счетов */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Тип
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Остаток
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Валюта
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                Документы
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts.map((account) => (
              <tr key={account.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {account.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(account.type)}`}>
                    {getTypeLabel(account.type)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatBalance(account.balance, account.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.currency}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    account.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  } ${account.isDefault ? 'underline' : ''}`}>
                    {account.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex flex-col space-y-0.5">
                    <div className="grid grid-cols-1 gap-0.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Перемещения:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'cashFlow', account._count?.cashFlow || 0)}
                          className={`text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline ${(account._count?.cashFlow || 0) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={(account._count?.cashFlow || 0) === 0}
                        >
                          {account._count?.cashFlow || 0}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Расходы:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'allExpenses', (account._count?.cashExpenses || 0) + (account._count?.expenses || 0))}
                          className={`text-xs font-medium text-red-600 hover:text-red-800 hover:underline ${((account._count?.cashExpenses || 0) + (account._count?.expenses || 0)) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={((account._count?.cashExpenses || 0) + (account._count?.expenses || 0)) === 0}
                        >
                          {(account._count?.cashExpenses || 0) + (account._count?.expenses || 0)}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Доходы:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'incomes', account._count?.incomes || 0)}
                          className={`text-xs font-medium text-green-600 hover:text-green-800 hover:underline ${(account._count?.incomes || 0) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={(account._count?.incomes || 0) === 0}
                        >
                          {account._count?.incomes || 0}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Игроки:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'playerPayments', account._count?.playerPayments || 0)}
                          className={`text-xs font-medium text-orange-600 hover:text-orange-800 hover:underline ${(account._count?.playerPayments || 0) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={(account._count?.playerPayments || 0) === 0}
                        >
                          {account._count?.playerPayments || 0}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Поставщики:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'settlements', account._count?.settlements || 0)}
                          className={`text-xs font-medium text-purple-600 hover:text-purple-800 hover:underline ${(account._count?.settlements || 0) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={(account._count?.settlements || 0) === 0}
                        >
                          {account._count?.settlements || 0}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">Покупатели:</span>
                        <button
                          onClick={() => handleDocumentClick(account, 'customerPayments', account._count?.customerPayments || 0)}
                          className={`text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline ${(account._count?.customerPayments || 0) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                          disabled={(account._count?.customerPayments || 0) === 0}
                        >
                          {account._count?.customerPayments || 0}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t pt-0.5">
                      <span className="text-xs font-semibold text-gray-700">Всего:</span>
                      <span className="text-xs font-bold text-gray-900">
                        {(account._count?.cashFlow || 0) + 
                         (account._count?.cashExpenses || 0) + 
                         (account._count?.incomes || 0) + 
                         (account._count?.playerPayments || 0) + 
                         (account._count?.expenses || 0) + 
                         (account._count?.settlements || 0) + 
                         (account._count?.customerPayments || 0)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(account)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleShowJson(account)}
                      className="text-green-600 hover:text-green-900"
                      title="Показать JSON"
                    >
                      📄
                    </button>
                    {(() => {
                      const totalDocuments = (account._count?.cashFlow || 0) + 
                                           (account._count?.cashExpenses || 0) + 
                                           (account._count?.incomes || 0) + 
                                           (account._count?.expenses || 0) + 
                                           (account._count?.settlements || 0) + 
                                           (account._count?.customerPayments || 0);
                      
                      // Если есть документы - кнопка удаления отсутствует
                      if (totalDocuments > 0) {
                        return null;
                      } else {
                        return (
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        );
                      }
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAccount ? 'Редактировать счет' : 'Добавить счет'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Название</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Тип</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="CASH">Касса</option>
                    <option value="BANK">Банковский счет</option>
                    <option value="CARD">Банковская карта</option>
                    <option value="EXTERNAL">Внешний счет</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Валюта</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="MDL">MDL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="RON">RON</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Активен
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
                    По умолчанию для типа {formData.type}
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingAccount ? 'Сохранить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для отображения документов */}
      {showDocumentsModal && selectedAccount && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Документы счета "{selectedAccount.name}"
                </h3>
                <button
                  onClick={closeDocumentsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Закрыть</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {loadingDocuments ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {documents.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Документы не найдены</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc, index) => {
                        // Определяем тип документа для объединенных расходов
                        const isCashExpense = doc.hasOwnProperty('method') // CashExpense имеет поле method
                        const isExpense = doc.hasOwnProperty('type') && !doc.hasOwnProperty('method') // Expense имеет поле type, но не method
                        
                        return (
                          <div key={index} className="border rounded-lg p-3 bg-gray-50">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900">
                                    {doc.number || `Документ ${index + 1}`}
                                  </p>
                                  {isCashExpense && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                      Наличные
                                    </span>
                                  )}
                                  {isExpense && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                      Общие
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {doc.description || 'Описание отсутствует'}
                                </p>
                                {doc.date && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(doc.date).toLocaleDateString('ru-RU')}
                                  </p>
                                )}
                              </div>
                              {doc.amount && (
                                <div className="text-right">
                                  <span className={`text-sm font-medium ${
                                    doc.amount > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {doc.amount > 0 ? '+' : ''}{doc.amount.toLocaleString('ru-RU')} {selectedAccount.currency}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для отображения JSON */}
      {showJsonModal && selectedAccountJson && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  JSON счёта "{selectedAccountJson.name}"
                </h3>
                <button
                  onClick={closeJsonModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Закрыть</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(selectedAccountJson, null, 2)}
                </pre>
              </div>
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={closeJsonModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CashAccounts
